/* KALK · Barra sticky aislada para la colección editorial. */
(() => {
  const ROOT_SELECTOR = '[data-collection-mobile-sticky-root]';
  const DIRECTION_THRESHOLD = 28;
  const HEADER_SELECTORS = [
    '.shopify-section-group-header-group',
    '.header-section',
    '.section-header',
    '#shopify-section-header',
    'header.custom_main_header',
    'header.header',
    '[data-sticky-header]'
  ];

  const rootStates = new WeakMap();
  const anchors = new WeakMap();
  let ticking = false;
  let resizeObserver;

  function isVisible(element) {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  function getHeaderOffset() {
    let offset = 0;

    HEADER_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (!isVisible(element)) return;
        const rect = element.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        offset = Math.max(offset, Math.round(rect.bottom));
      });
    });

    return Math.min(Math.max(offset, 0), 320);
  }

  function getRoots() {
    return Array.from(document.querySelectorAll(ROOT_SELECTOR));
  }

  function getState(root) {
    if (!rootStates.has(root)) {
      rootStates.set(root, {
        direction: '',
        lastY: Math.max(window.scrollY || window.pageYOffset || 0, 0)
      });
    }
    return rootStates.get(root);
  }

  function getAnchor(root) {
    const target = root.querySelector('[data-collection-mobile-sticky-shell]');
    if (!target) return null;

    let anchor = anchors.get(root);
    if (!anchor || !anchor.isConnected || anchor.nextElementSibling !== target) {
      anchor?.remove();
      anchor = document.createElement('span');
      anchor.setAttribute('data-collection-sticky-anchor', '');
      anchor.setAttribute('aria-hidden', 'true');
      anchor.style.cssText = [
        'display:block',
        'position:relative',
        'height:0',
        'min-height:0',
        'max-height:0',
        'margin:0',
        'padding:0',
        'border:0',
        'overflow:hidden',
        'pointer-events:none'
      ].join(';');
      target.before(anchor);
      anchors.set(root, anchor);
    }

    return anchor;
  }

  function setScrollState(root, state) {
    root.dataset.collectionScrollState = state;
  }

  function syncMeasurements(root, headerOffset) {
    const bubbles = root.querySelector('[data-collection-mobile-bubbles]');
    root.classList.add('collection-mobile-sticky-active');
    root.style.setProperty('--collection-mobile-header-offset', `${headerOffset}px`);
    root.style.setProperty(
      '--collection-mobile-bubbles-height',
      `${bubbles ? Math.round(bubbles.offsetHeight) : 0}px`
    );
  }

  function updateRoot(root, currentY, headerOffset) {
    const state = getState(root);
    const bubbles = root.querySelector('[data-collection-mobile-bubbles]');
    const anchor = getAnchor(root);

    syncMeasurements(root, headerOffset);

    if (!anchor || anchor.getBoundingClientRect().top >= headerOffset - 1) {
      setScrollState(root, 'top');
      state.direction = '';
      state.lastY = currentY;
      return;
    }

    if (!bubbles?.querySelector('li')) {
      setScrollState(root, 'down');
      state.direction = 'down';
      state.lastY = currentY;
      return;
    }

    const distance = currentY - state.lastY;
    if (!state.direction) {
      state.direction = 'down';
      setScrollState(root, 'down');
      state.lastY = currentY;
      return;
    }

    if (Math.abs(distance) < DIRECTION_THRESHOLD) return;

    state.direction = distance > 0 ? 'down' : 'up';
    state.lastY = currentY;
    setScrollState(root, state.direction);
  }

  function update() {
    ticking = false;
    const currentY = Math.max(window.scrollY || window.pageYOffset || 0, 0);
    const headerOffset = getHeaderOffset();
    getRoots().forEach((root) => updateRoot(root, currentY, headerOffset));
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function observeLayout() {
    if (!window.ResizeObserver) return;
    resizeObserver ||= new ResizeObserver(requestUpdate);

    getRoots().forEach((root) => {
      resizeObserver.observe(root);
      const bubbles = root.querySelector('[data-collection-mobile-bubbles]');
      if (bubbles) resizeObserver.observe(bubbles);
    });

    HEADER_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => resizeObserver.observe(element));
    });
  }

  function initialise() {
    if (!getRoots().length) return;
    observeLayout();
    requestUpdate();
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  window.addEventListener('pageshow', initialise);
  document.addEventListener('load', requestUpdate, true);
  document.addEventListener('loadedmetadata', requestUpdate, true);
  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('shopify:section:reorder', initialise);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
